import type { ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import type { HeaderSection } from '@/blocks/domain/types';
import { canvasSectionDragId } from '@/blocks/domain/helpers/canvasDragIds';
import { SectionInsetFrame } from '@/document-render/presentation/blocks/fields/EditorInsetFrame';
import { CanvasDragHandle } from './CanvasDragHandle';
import { CanvasDropIndicator } from './CanvasDropIndicator';
import { CanvasSectionLayoutSwitcher } from './CanvasSectionLayoutSwitcher';
import { useCanvasDragState, useCanvasStructure, type CanvasStructureApi } from './CanvasStructureContext';
import { sectionChromeClearancePx } from './editingChromeGeometry';

export interface CanvasSectionShellProps {
  section: HeaderSection;
  /** The 1-based position, for the accessible names ("Section 2"). */
  position: number;
  /** The selected element is in this section - its chrome stays visible, not only on hover. */
  containsSelection: boolean;
  /**
   * The selected element is the first of one of this section's columns. Its name tab and ⋮ reach
   * above the section's top edge, so the Section tab and the column switcher stack above them
   * instead of covering them (LLAMS-699: the first element could not be dragged).
   */
  selectionOnFirstRow?: boolean;
  children: ReactNode;
}

const testIdFor = (id: string) => `document-render:fields:section-${id}`;

/** Above the previous section's insertion line (z 3), which overhangs this tab by 7px. */
const SECTION_CHROME_Z_INDEX = 4;

/**
 * One section of a header on the page. Without the block editor's structure API it is today's
 * inset frame. With it, the section is a drag source / drop target and shows (on hover, or while
 * it holds the selected element) a dashed outline and a Section tab. The column switcher
 * stays hidden until the pointer is over the section, sitting on the top-right corner.
 */
export function CanvasSectionShell(props: CanvasSectionShellProps) {
  const api = useCanvasStructure();
  if (!api) return <SectionInsetFrame testId={testIdFor(props.section.id)}>{props.children}</SectionInsetFrame>;
  return <StructuredSection {...props} api={api} />;
}

function StructuredSection({
  section,
  position,
  containsSelection,
  selectionOnFirstRow = false,
  children,
  api
}: CanvasSectionShellProps & { api: CanvasStructureApi }) {
  const dragId = canvasSectionDragId(section.id);
  const drag = useDraggable({ id: dragId, data: { name: `Section ${position}` } });
  const drop = useDroppable({ id: dragId, data: { name: `Section ${position}` } });
  const dragState = useCanvasDragState();
  const testId = testIdFor(section.id);
  const name = `Section ${position}`;
  const showIndicator = dragState.overId === dragId && dragState.activeId !== dragId;
  const clearance = sectionChromeClearancePx(selectionOnFirstRow);

  return (
    <Box
      ref={(node: HTMLElement | null) => {
        drag.setNodeRef(node);
        drop.setNodeRef(node);
      }}
      data-canvas-section={section.id}
      data-chrome-visible={containsSelection ? 'true' : undefined}
      data-first-row-selected={selectionOnFirstRow ? 'true' : undefined}
      sx={{
        position: 'relative',
        opacity: drag.isDragging ? 0.4 : 1,
        '& > [data-section-chrome], & > [data-section-outline]': {
          opacity: containsSelection ? 1 : 0,
          pointerEvents: containsSelection ? 'auto' : 'none'
        },
        '&:hover > [data-section-chrome], &:focus-within > [data-section-chrome]': { opacity: 1, pointerEvents: 'auto' },
        '&:hover > [data-section-outline], &:focus-within > [data-section-outline]': { opacity: 1 },
        '& > [data-section-outline]': { pointerEvents: 'none' },
        '& > [data-section-columns]': { opacity: 0, pointerEvents: 'none' },
        '&:hover > [data-section-columns], & > [data-section-columns]:focus-within': { opacity: 1, pointerEvents: 'auto' },
        // The column ⋮ menus (LLAMS-699) live inside the columns: shown with the section's chrome, or while one is open.
        '& [data-column-chrome]': { opacity: 0, pointerEvents: 'none' },
        '&:hover [data-column-chrome], &:focus-within [data-column-chrome], & [data-column-chrome][data-menu-open]': {
          opacity: 1,
          pointerEvents: 'auto'
        }
      }}
    >
      <SectionInsetFrame testId={testId}>{children}</SectionInsetFrame>
      <Box
        data-section-outline
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          border: 'var(--border-width-xs) dashed var(--border-focus)',
          borderRadius: 'var(--border-radius-none)',
          pointerEvents: 'none'
        }}
      />
      <Box
        data-section-chrome
        data-testid={`${testId}:tab`}
        sx={{
          position: 'absolute',
          bottom: '100%',
          mb: `${clearance}px`,
          left: -1,
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--size-4, 4px)',
          px: 'var(--size-6, 6px)',
          py: '1px',
          bgcolor: 'var(--surface-neutral-container-secondary)',
          color: 'var(--border-focus)',
          border: 'var(--border-width-xs) dashed var(--border-focus)',
          borderBottom: 0,
          borderRadius: 'var(--border-radius-none)',
          zIndex: SECTION_CHROME_Z_INDEX
        }}
      >
        <CanvasDragHandle
          label={`Move ${name}`}
          setActivatorNodeRef={drag.setActivatorNodeRef}
          onPointerDown={drag.listeners?.onPointerDown as Parameters<typeof CanvasDragHandle>[0]['onPointerDown']}
          onMove={(delta) => api.moveSectionBy(section.id, delta)}
          testId={`${testId}:drag-handle`}
        />
        <Typography component="span" sx={{ fontSize: 11, fontWeight: 600, lineHeight: 1.4 }}>
          Section
        </Typography>
      </Box>
      <Box
        data-section-columns
        sx={{
          position: 'absolute',
          right: 0,
          zIndex: SECTION_CHROME_Z_INDEX,
          // Centred on the top edge, or (a first-row element selected) sitting on top of its chrome.
          ...(clearance > 0 ? { bottom: '100%', mb: `${clearance}px` } : { top: 0, transform: 'translateY(-50%)' })
        }}
      >
        <CanvasSectionLayoutSwitcher
          layout={section.layout}
          sectionName={name}
          onChange={(layout) => api.setSectionLayout(section.id, layout)}
          testId={`${testId}:layout`}
        />
      </Box>
      {showIndicator ? <CanvasDropIndicator edge={dragState.after ? 'bottom' : 'top'} /> : null}
    </Box>
  );
}
